/**
 * TreasureCodeWidget — vanity share code + 3-stage referral progress.
 * Embed in Wallet page or Refer & Earn page.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Copy, Share2, Pencil, Coins } from "lucide-react";
import { toast } from "sonner";

export default function TreasureCodeWidget() {
  const [data, setData] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/treasure/share-code");
      setData(data);
      setNewCode(data.share_code);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const code = data.share_code;
  const url = data.share_url;

  const copyCode = () => { navigator.clipboard.writeText(code); toast.success("Code copied"); };
  const copyUrl = () => { navigator.clipboard.writeText(url); toast.success("Share link copied"); };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join Treasurescare",
          text: `Use my treasure code ${code} and get 50 Gems (≈ £0.50) on signup!`,
          url,
        });
      } catch {}
    } else {
      copyUrl();
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: d } = await api.post("/treasure/share-code/customise", { code: newCode.toUpperCase() });
      toast.success(`Code changed to ${d.share_code}`);
      setShowEdit(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
    setSaving(false);
  };

  return (
    <Card className="overflow-hidden relative" data-testid="treasure-code-widget">
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-secondary/10 blur-3xl" />
      <CardHeader className="relative z-10">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-secondary" />
          Your Treasure Code
        </CardTitle>
      </CardHeader>
      <CardContent className="relative z-10 space-y-4">
        {/* The Code */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-2xl font-mono font-semibold tracking-wide bg-primary/10 text-primary rounded-lg px-4 py-2" data-testid="treasure-code-value">
            {code}
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={copyCode} data-testid="copy-code-btn"><Copy className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant="outline" onClick={share} data-testid="share-code-btn"><Share2 className="w-3.5 h-3.5" /></Button>
            {!data.edited && (
              <Button size="sm" variant="outline" onClick={() => setShowEdit(true)} data-testid="edit-code-btn"><Pencil className="w-3.5 h-3.5" /></Button>
            )}
          </div>
          {data.edited && <Badge variant="outline" className="text-[10px]">Locked (edited once)</Badge>}
        </div>

        {/* Share URL */}
        <div className="text-xs text-muted-foreground break-all">
          <span className="opacity-70">Share link: </span>
          <button onClick={copyUrl} className="underline hover:text-foreground" data-testid="share-url">{url}</button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Referrals</div>
            <div className="text-xl font-mono font-semibold mt-0.5" data-testid="treasure-referral-count">{data.stats?.referrals || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Gems earned</div>
            <div className="text-xl font-mono font-semibold mt-0.5 text-secondary flex items-center gap-1">
              <Coins className="w-4 h-4" />{data.stats?.gems_earned || 0}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">≈ Value</div>
            <div className="text-xl font-mono font-semibold mt-0.5 text-emerald-500">£{(data.stats?.value_gbp || 0).toFixed(2)}</div>
          </div>
        </div>

        {/* Reward legend */}
        <div className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-3 space-y-1">
          <div className="font-semibold text-foreground mb-1">You earn per friend:</div>
          <div>• <b>+15 Gems</b> when they sign up with your code</div>
          <div>• <b>+50 Gems</b> when they complete their first repair</div>
          <div>• <b>+150 Gems</b> when they upgrade to any paid plan</div>
          <div className="text-[10px] opacity-70 mt-1">100 Gems ≈ £1 · max 12 rewarded referrals per year</div>
        </div>
      </CardContent>

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Customise your treasure code</DialogTitle></DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3">
            <div>
              <Input
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                placeholder="WORD-WORD-NN"
                data-testid="new-code-input"
                autoFocus
              />
              <div className="text-xs text-muted-foreground mt-2">
                Format: <span className="font-mono">WORD-WORD-NN</span> — uppercase letters/digits only.
                Examples: <code>LUCKY-OTTER-42</code>, <code>ROYAL-FOX-7</code>.
                <br /><b>You can only change this once</b>, so pick something memorable.
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="save-code-btn">
                {saving ? "Saving…" : "Lock in this code"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
